import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, Save, Eye, EyeOff, Phone, FileText, Users, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const AdmissionsPageManagement = () => {
  const [activeTab, setActiveTab] = useState('steps'); // 'steps', 'documents', or 'faqs'
  const [steps, setSteps] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemType, setDeleteItemType] = useState(null); // 'faq', 'step', 'document'
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [faqFormData, setFaqFormData] = useState({
    question: '',
    answer: '',
    order_index: 0,
    is_active: true,
  });
  const [stepFormData, setStepFormData] = useState({
    title: '',
    description: '',
    icon_name: 'Phone',
    order_index: 0,
  });
  const [documentFormData, setDocumentFormData] = useState({
    name: '',
    order_index: 0,
  });

  useEffect(() => {
    fetchFaqs();
    fetchSteps();
    fetchDocuments();
  }, []);


  const fetchFaqs = async () => {
    try {
      const response = await fetch('http://localhost/api/faqs.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'FAQ\'lar yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // is_active değerlerini normalize et
      const normalizedData = data.map(item => ({
        ...item,
        is_active: item.is_active === 1 || item.is_active === '1' || item.is_active === true ? 1 : 0,
      }));
      
      // Remove duplicates based on question (keep the first occurrence)
      const uniqueFaqs = normalizedData.filter((faq, index, self) => 
        index === self.findIndex((f) => f.question === faq.question)
      );
      setFaqs(uniqueFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('FAQ\'lar yüklenirken hata oluştu');
    }
  };

  const handleFaqInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFaqFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'order_index' ? parseInt(value) || 0 : value),
    }));
  };

  const handleFaqSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingFaq ? 'PUT' : 'POST';
      const url = editingFaq 
        ? `http://localhost/api/faqs.php?id=${editingFaq.id}`
        : 'http://localhost/api/faqs.php';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question: faqFormData.question,
          answer: faqFormData.answer,
          order_index: faqFormData.order_index,
          is_active: 1, // Her zaman aktif olarak kaydet
        }),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'SSS kaydedilirken hata oluştu');
        return;
      }

      toast.success(editingFaq ? 'SSS güncellendi' : 'SSS eklendi');
      setIsFaqModalOpen(false);
      setEditingFaq(null);
      setFaqFormData({
        question: '',
        answer: '',
        order_index: 0,
        is_active: true,
      });
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('FAQ kaydedilirken hata oluştu');
    }
  };

  const handleFaqEdit = (faq) => {
    setEditingFaq(faq);
    setFaqFormData({
      question: faq.question || '',
      answer: faq.answer || '',
      order_index: faq.order_index || 0,
      is_active: faq.is_active === 1 || faq.is_active === true,
    });
    setIsFaqModalOpen(true);
  };

  const handleFaqDelete = (id) => {
    if (!id) {
      console.error('FAQ ID is missing');
      toast.error('SSS ID bulunamadı');
      return;
    }
    setDeleteItemId(id);
    setDeleteItemType('faq');
    setIsDeleteModalOpen(true);
  };

  const handleStepDelete = (id) => {
    setDeleteItemId(id);
    setDeleteItemType('step');
    setIsDeleteModalOpen(true);
  };

  const handleDocumentDelete = (id) => {
    setDeleteItemId(id);
    setDeleteItemType('document');
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId || !deleteItemType) return;

    try {
      let url = '';
      let successMessage = '';
      
      if (deleteItemType === 'faq') {
        url = `http://localhost/api/faqs.php?id=${deleteItemId}`;
        successMessage = 'SSS silindi';
      } else if (deleteItemType === 'step') {
        url = `http://localhost/api/admission-data.php?type=steps&id=${deleteItemId}`;
        successMessage = 'Adım silindi';
      } else if (deleteItemType === 'document') {
        url = `http://localhost/api/admission-data.php?type=documents&id=${deleteItemId}`;
        successMessage = 'Belge silindi';
      }

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        console.error('Error parsing response:', e);
      }

      if (!response.ok) {
        toast.error(data.error || 'Silme işlemi başarısız');
        return;
      }

      toast.success(successMessage);
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      setDeleteItemType(null);
      
      if (deleteItemType === 'faq') {
        fetchFaqs();
      } else if (deleteItemType === 'step') {
        fetchSteps();
      } else if (deleteItemType === 'document') {
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Silme işlemi sırasında hata oluştu: ' + error.message);
    }
  };

  const openAddFaqModal = () => {
    setEditingFaq(null);
    setFaqFormData({
      question: '',
      answer: '',
      order_index: faqs.length,
      is_active: true,
    });
    setIsFaqModalOpen(true);
  };

  const fetchSteps = async () => {
    try {
      const response = await fetch('http://localhost/api/admission-data.php?type=steps', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Adımlar yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setSteps(data);
    } catch (error) {
      console.error('Error fetching steps:', error);
      toast.error('Adımlar yüklenirken hata oluştu');
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch('http://localhost/api/admission-data.php?type=documents', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Belgeler yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Belgeler yüklenirken hata oluştu');
    }
  };

  const handleStepInputChange = (e) => {
    const { name, value } = e.target;
    setStepFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' ? parseInt(value) || 0 : value,
    }));
  };

  const handleStepSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingStep ? 'PUT' : 'POST';
      const url = editingStep 
        ? `http://localhost/api/admission-data.php?type=steps&id=${editingStep.id}`
        : 'http://localhost/api/admission-data.php?type=steps';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(stepFormData),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Adım kaydedilirken hata oluştu');
        return;
      }

      toast.success(editingStep ? 'Adım güncellendi' : 'Adım eklendi');
      setIsStepModalOpen(false);
      setEditingStep(null);
      setStepFormData({
        title: '',
        description: '',
        icon_name: 'Phone',
        order_index: 0,
      });
      fetchSteps();
    } catch (error) {
      console.error('Error saving step:', error);
      toast.error('Adım kaydedilirken hata oluştu');
    }
  };

  const handleStepEdit = (step) => {
    setEditingStep(step);
    setStepFormData({
      title: step.title || '',
      description: step.description || '',
      icon_name: step.icon_name || 'Phone',
      order_index: step.order_index || 0,
    });
    setIsStepModalOpen(true);
  };


  const openAddStepModal = () => {
    setEditingStep(null);
    setStepFormData({
      title: '',
      description: '',
      icon_name: 'Phone',
      order_index: steps.length,
    });
    setIsStepModalOpen(true);
  };

  const handleDocumentInputChange = (e) => {
    const { name, value } = e.target;
    setDocumentFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' ? parseInt(value) || 0 : value,
    }));
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();

    try {
      const method = editingDocument ? 'PUT' : 'POST';
      const url = editingDocument 
        ? `http://localhost/api/admission-data.php?type=documents&id=${editingDocument.id}`
        : 'http://localhost/api/admission-data.php?type=documents';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(documentFormData),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'Belge kaydedilirken hata oluştu');
        return;
      }

      toast.success(editingDocument ? 'Belge güncellendi' : 'Belge eklendi');
      setIsDocumentModalOpen(false);
      setEditingDocument(null);
      setDocumentFormData({
        name: '',
        order_index: 0,
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Belge kaydedilirken hata oluştu');
    }
  };

  const handleDocumentEdit = (document) => {
    setEditingDocument(document);
    setDocumentFormData({
      name: document.name || '',
      order_index: document.order_index || 0,
    });
    setIsDocumentModalOpen(true);
  };


  const openAddDocumentModal = () => {
    setEditingDocument(null);
    setDocumentFormData({
      name: '',
      order_index: documents.length,
    });
    setIsDocumentModalOpen(true);
  };

  const getStepIcon = (iconName) => {
    const icons = {
      Phone: <Phone className="w-6 h-6" />,
      FileText: <FileText className="w-6 h-6" />,
      Users: <Users className="w-6 h-6" />,
      CheckCircle: <CheckCircle className="w-6 h-6" />,
      Calendar: <Calendar className="w-6 h-6" />,
    };
    return icons[iconName] || <Phone className="w-6 h-6" />;
  };

  const getAddButton = () => {
    switch (activeTab) {
      case 'steps':
        return (
          <Button
            onClick={openAddStepModal}
            className="bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700 text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Yeni Adım
          </Button>
        );
      case 'documents':
        return (
          <Button
            onClick={openAddDocumentModal}
            className="bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700 text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Yeni Belge
          </Button>
        );
      case 'faqs':
        return (
          <Button
            onClick={openAddFaqModal}
            className="bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700 text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Yeni Soru
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <AdminLayout
      addButton={getAddButton()}
    >
      <div>
        {/* Tabs */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-0 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('steps')}
            className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
              activeTab === 'steps'
                ? 'text-red-900 border-red-900'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Başvuru Süreci
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
              activeTab === 'documents'
                ? 'text-red-900 border-red-900'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Gerekli Belgeler
          </button>
          <button
            onClick={() => setActiveTab('faqs')}
            className={`px-4 sm:px-6 py-3 font-semibold transition-colors border-b-2 text-center ${
              activeTab === 'faqs'
                ? 'text-red-900 border-red-900'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Sıkça Sorulan Sorular
          </button>
        </div>

        {/* Steps Tab */}
        {activeTab === 'steps' && (
        <div className="mt-6">
          {steps.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz adım yok</h3>
              <p className="text-gray-600 mb-6">Yeni bir adım ekleyerek başlayın</p>
              <Button onClick={openAddStepModal} className="bg-gradient-to-r from-red-900 to-red-800">
                İlk Adımı Ekle
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {steps
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((step, index) => (
                  <div
                    key={step.id}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-red-900/10 text-red-900 text-xs font-semibold rounded-full">
                            Sıra: {index + 1}
                          </span>
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full flex items-center gap-1.5">
                            {getStepIcon(step.icon_name)}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title || 'Başlıksız'}</h3>
                        <p className="text-gray-600 text-sm">{step.description || 'Açıklama yok'}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => handleStepEdit(step)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit size={16} className="mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          onClick={() => handleStepDelete(step.id)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
        <div className="mt-6">
          {documents.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz belge yok</h3>
              <p className="text-gray-600 mb-6">Yeni bir belge ekleyerek başlayın</p>
              <Button onClick={openAddDocumentModal} className="bg-gradient-to-r from-red-900 to-red-800">
                İlk Belgeyi Ekle
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {documents
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((document, index) => (
                  <div
                    key={document.id}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-red-900/10 text-red-900 text-xs font-semibold rounded-full">
                            Sıra: {index + 1}
                          </span>
                        </div>
                        <p className="text-gray-900 text-base">{document.name || 'Belge adı yok'}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => handleDocumentEdit(document)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit size={16} className="mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          onClick={() => handleDocumentDelete(document.id)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        )}

        {/* FAQs Tab */}
        {activeTab === 'faqs' && (
        <div className="mt-6">
          {faqs.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">❓</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz soru yok</h3>
              <p className="text-gray-600 mb-6">Yeni bir soru ekleyerek başlayın</p>
              <Button onClick={openAddFaqModal} className="bg-gradient-to-r from-red-900 to-red-800">
                İlk Soruyu Ekle
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {faqs
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((faq, index) => (
                  <div
                    key={faq.id}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-3 py-1 bg-red-900/10 text-red-900 text-xs font-semibold rounded-full">
                            Sıra: {index + 1}
                          </span>
                        </div>
                        <div className="space-y-2 mb-3">
                          <div>
                            <span className="text-xs font-semibold text-gray-500">Soru:</span>
                            <p className="text-lg font-bold text-gray-900">{faq.question || 'Soru yok'}</p>
                          </div>
                          <div>
                            <span className="text-xs font-semibold text-gray-500">Cevap:</span>
                            <p className="text-gray-700 text-sm line-clamp-2">{faq.answer || 'Cevap yok'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          onClick={() => handleFaqEdit(faq)}
                          variant="outline"
                          size="sm"
                        >
                          <Edit size={16} className="mr-1" />
                          Düzenle
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (faq.id) {
                              handleFaqDelete(faq.id);
                            } else {
                              console.error('FAQ ID is missing:', faq);
                              toast.error('FAQ ID bulunamadı');
                            }
                          }}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Add/Edit FAQ Modal */}
      <Dialog open={isFaqModalOpen} onOpenChange={setIsFaqModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'FAQ Düzenle' : 'Yeni FAQ Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFaqSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Soru *</label>
              <Input
                name="question"
                value={faqFormData.question}
                onChange={handleFaqInputChange}
                placeholder="Soru metni"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cevap *</label>
              <Textarea
                name="answer"
                value={faqFormData.answer}
                onChange={handleFaqInputChange}
                placeholder="Cevap metni"
                rows={6}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sıra</label>
              <Input
                type="number"
                name="order_index"
                value={faqFormData.order_index}
                onChange={handleFaqInputChange}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
              >
                <Save size={16} className="mr-2" />
                {editingFaq ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFaqModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Step Modal */}
      <Dialog open={isStepModalOpen} onOpenChange={setIsStepModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingStep ? 'Adım Düzenle' : 'Yeni Adım Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStepSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık *</label>
              <Input
                name="title"
                value={stepFormData.title}
                onChange={handleStepInputChange}
                placeholder="Adım başlığı"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Açıklama *</label>
              <Textarea
                name="description"
                value={stepFormData.description}
                onChange={handleStepInputChange}
                placeholder="Adım açıklaması"
                rows={4}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">İkon Seçin</label>
              <div className="grid grid-cols-5 gap-3">
                {[
                  { name: 'Phone', icon: <Phone className="w-6 h-6" />, label: 'Telefon' },
                  { name: 'FileText', icon: <FileText className="w-6 h-6" />, label: 'Dosya' },
                  { name: 'Users', icon: <Users className="w-6 h-6" />, label: 'Kullanıcılar' },
                  { name: 'CheckCircle', icon: <CheckCircle className="w-6 h-6" />, label: 'Onay' },
                  { name: 'Calendar', icon: <Calendar className="w-6 h-6" />, label: 'Takvim' },
                ].map((iconOption) => (
                  <button
                    key={iconOption.name}
                    type="button"
                    onClick={() => setStepFormData(prev => ({ ...prev, icon_name: iconOption.name }))}
                    className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      stepFormData.icon_name === iconOption.name
                        ? 'border-red-900 bg-red-900/10 scale-105'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`${stepFormData.icon_name === iconOption.name ? 'text-red-900' : 'text-gray-600'}`}>
                      {iconOption.icon}
                    </div>
                    <span className={`text-xs font-medium ${
                      stepFormData.icon_name === iconOption.name ? 'text-red-900' : 'text-gray-600'
                    }`}>
                      {iconOption.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sıra</label>
              <Input
                type="number"
                name="order_index"
                value={stepFormData.order_index}
                onChange={handleStepInputChange}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
              >
                <Save size={16} className="mr-2" />
                {editingStep ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsStepModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Document Modal */}
      <Dialog open={isDocumentModalOpen} onOpenChange={setIsDocumentModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDocument ? 'Belge Düzenle' : 'Yeni Belge Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDocumentSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Belge Adı *</label>
              <Input
                name="name"
                value={documentFormData.name}
                onChange={handleDocumentInputChange}
                placeholder="Belge adı"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sıra</label>
              <Input
                type="number"
                name="order_index"
                value={documentFormData.order_index}
                onChange={handleDocumentInputChange}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-red-900 to-red-800 hover:from-red-800 hover:to-red-700"
              >
                <Save size={16} className="mr-2" />
                {editingDocument ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDocumentModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdmissionsPageManagement;

